import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const API = "https://booksmart-production-f6dc.up.railway.app";
const getToken = () => sessionStorage.getItem("token");

const FONT_UI = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const FONT_DATA = "'DM Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

const s = {
  page: { fontFamily: FONT_UI, background: "#fafaf9", minHeight: "100vh", color: "#18181b", width: "100%", overflowX: "hidden" },
  header: { borderBottom: "1px solid #e7e5e4", padding: "0 32px", display: "flex", alignItems: "center", height: 56, background: "#ffffff", gap: 16 },
  backBtn: { background: "transparent", color: "#52525b", border: "1px solid #e7e5e4", borderRadius: 8, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em", textTransform: "uppercase" },
  logo: { fontSize: 18, fontWeight: 700, letterSpacing: "0.02em", color: "#18181b", textTransform: "uppercase" },
  logoBeta: { fontSize: 10, background: "#f4f4f5", color: "#52525b", padding: "2px 6px", borderRadius: 6, marginLeft: 8, letterSpacing: "0.02em" },
  main: { padding: "48px 32px", maxWidth: 1100, margin: "0 auto" },
  eyebrow: { fontSize: 11, letterSpacing: "0.02em", textTransform: "uppercase", color: "#52525b", marginBottom: 8 },
  title: { fontSize: 24, fontWeight: 700, color: "#18181b", marginBottom: 6 },
  subtitle: { fontSize: 13, color: "#52525b", marginBottom: 48 },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, alignItems: "start" },
  card: (highlighted, isCurrent) => ({ background: "#ffffff", border: `1px solid ${isCurrent ? "#1f8a4c" : highlighted ? "#18181b" : "#e7e5e4"}`, borderRadius: 10, padding: "28px 24px", display: "flex", flexDirection: "column", boxShadow: "0 1px 2px rgba(0,0,0,.04)" }),
  planName: { fontSize: 11, fontWeight: 700, letterSpacing: "0.02em", textTransform: "uppercase", color: "#52525b", marginBottom: 8 },
  price: { fontSize: 36, fontWeight: 700, color: "#18181b", lineHeight: 1, fontFamily: FONT_DATA },
  priceDetail: { fontSize: 12, color: "#52525b", marginTop: 4, marginBottom: 24 },
  featureList: { listStyle: "none", padding: 0, margin: "0 0 28px 0", flex: 1 },
  featureItem: { fontSize: 12, color: "#3f3f46", padding: "7px 0", borderBottom: "1px solid #f4f4f5", display: "flex", alignItems: "center", gap: 8 },
  check: { color: "#1f8a4c", fontSize: 11, flexShrink: 0 },
  btnSubscribe: (inFlight) => ({ background: inFlight ? "#f4f4f5" : "#18181b", color: inFlight ? "#a1a1aa" : "#ffffff", border: `1px solid ${inFlight ? "#e7e5e4" : "#18181b"}`, borderRadius: 8, padding: "10px 20px", fontWeight: 600, fontSize: 12, letterSpacing: "0.02em", textTransform: "uppercase", cursor: inFlight ? "default" : "pointer", fontFamily: "inherit", width: "100%" }),
  btnCurrent: { background: "#f0fdf4", color: "#1f8a4c", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 20px", fontWeight: 600, fontSize: 12, letterSpacing: "0.02em", textTransform: "uppercase", cursor: "default", fontFamily: "inherit", width: "100%" },
  btnEnterprise: { background: "transparent", color: "#18181b", border: "1px solid #18181b33", borderRadius: 8, padding: "10px 20px", fontWeight: 600, fontSize: 12, letterSpacing: "0.02em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", width: "100%", textDecoration: "none", display: "block", textAlign: "center", boxSizing: "border-box" },
  input: { background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 8, padding: "8px 12px", color: "#18181b", fontSize: 14, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box" },
  select: { background: "#ffffff", border: "1px solid #e7e5e4", borderRadius: 8, padding: "8px 12px", color: "#18181b", fontSize: 14, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box" },
  error: { fontSize: 11, color: "#b91c1c", marginTop: 10, padding: "8px 10px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 },
  note: { fontSize: 12, color: "#a1a1aa", textAlign: "center", marginTop: 40 },
  langBtn: { background: "none", border: "1px solid #e7e5e4", borderRadius: 8, color: "#18181b", fontSize: 11, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em" },
};

const PLANS = [
  {
    key: "starter",
    name: "STARTER",
    price: "$89",
    currency: "CAD",
    period: "/month",
    features: [
      "2 seats",
      "25 active bookings monitored",
      "60-minute check intervals",
      "Email alerts on status changes",
      "30-day alert history",
      "12 supported carriers",
    ],
    isSales: false,
    recommended: false,
  },
  {
    key: "business",
    name: "BUSINESS",
    price: "$299",
    currency: "CAD",
    period: "/month",
    features: [
      "5 seats",
      "200 active bookings monitored",
      "30-minute check intervals",
      "SMS alerts",
      "Outbound webhooks",
      "Client-facing status page",
    ],
    isSales: false,
    recommended: true,
  },
  {
    key: "enterprise",
    name: "ENTERPRISE",
    price: "Custom",
    currency: "",
    period: "",
    features: [
      "Unlimited seats",
      "Unlimited bookings monitored",
      "15-minute check intervals",
      "Full API access",
      "White-label support",
      "365-day alert history",
    ],
    isSales: true,
    recommended: false,
  },
];

function PlanBadge({ text, color }) {
  return (
    <div style={{
      display: "inline-block",
      background: color + "1a",
      color,
      border: `1px solid ${color}33`,
      borderRadius: 6,
      padding: "2px 8px",
      fontSize: 10,
      letterSpacing: "0.02em",
      textTransform: "uppercase",
      marginBottom: 16,
      fontFamily: "inherit",
    }}>
      {text}
    </div>
  );
}

export default function PricingPage({ onBack, currentPlan, onGoToSignup }) {
  const { t } = useTranslation();
  const [lang, setLang] = useState(localStorage.getItem("booksmart_lang") || "en");
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const toggleLang = () => {
    const next = lang === "en" ? "fr" : "en";
    i18n.changeLanguage(next);
    localStorage.setItem("booksmart_lang", next);
    setLang(next);
  };
  const [loading, setLoading] = useState(null);  // plan key currently in flight
  const [errors, setErrors]   = useState({});    // { [planKey]: string }
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ full_name: "", company_name: "", work_email: "", bookings: "201-500", carriers: [], message: "" });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactMsg, setContactMsg] = useState({ text: "", type: "" });

  const subscribe = async (planKey) => {
    if (!getToken()) {
      sessionStorage.setItem("pending_plan", planKey);
      onGoToSignup();
      return;
    }

    setLoading(planKey);
    setErrors(prev => ({ ...prev, [planKey]: null }));

    // Persist so OverviewTab can show the plan name in the success banner.
    sessionStorage.setItem("pending_plan", planKey);

    try {
      const res = await fetch(`${API}/billing/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ plan: planKey }),
      });

      if (res.status === 401) {
        sessionStorage.clear();
        window.location.reload();
        return;
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create checkout session.");

      // Navigate to Stripe — this unloads the page, so no cleanup needed.
      window.location.href = data.checkout_url;
    } catch (e) {
      sessionStorage.removeItem("pending_plan");
      setErrors(prev => ({ ...prev, [planKey]: e.message }));
      setLoading(null);
    }
  };

  const handleContactSubmit = async () => {
    setContactLoading(true);
    setContactMsg({ text: "", type: "" });
    try {
      const r = await fetch(`${API}/contact/enterprise`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactForm),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || t('pricing.contactError'));
      }
      setContactMsg({ text: t('pricing.contactSuccess'), type: "ok" });
      setContactForm({ full_name: "", company_name: "", work_email: "", bookings: "201-500", carriers: [], message: "" });
      setTimeout(() => setContactOpen(false), 2000);
    } catch (e) {
      setContactMsg({ text: e.message, type: "error" });
    } finally {
      setContactLoading(false);
    }
  };

  const toggleCarrier = (carrier) => {
    setContactForm(f => ({
      ...f,
      carriers: f.carriers.includes(carrier) ? f.carriers.filter(c => c !== carrier) : [...f.carriers, carrier],
    }));
  };

  const CARRIER_OPTIONS = ["Evergreen", "Yang Ming", "ONE", "Hapag-Lloyd", "Maersk", "CMA CGM", "MSC", "Other"];

  return (
    <div style={s.page}>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&display=swap"
        rel="stylesheet"
      />
      <header style={s.header}>
        <button style={s.backBtn} onClick={onBack}>{getToken() ? "← Dashboard" : "← Home"}</button>
        <span style={s.logo}>Booksmart</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {!isMobile && <button style={s.langBtn} onClick={toggleLang}>{lang === "en" ? "FR" : "EN"}</button>}
        </div>
      </header>

      <main style={{ ...s.main, padding: isMobile ? "32px 16px" : s.main.padding }}>
        <div style={s.eyebrow}>{t('pricing.title')}</div>
        <div style={s.title}>{t('pricing.subtitle')}</div>
        <div style={s.subtitle}>{t('pricing.cadNote')}</div>

        <div style={{ ...s.grid, gridTemplateColumns: isMobile ? "1fr" : s.grid.gridTemplateColumns }}>
          {PLANS.map(plan => {
            const isCurrent = currentPlan === plan.key;
            const inFlight  = loading === plan.key;
            const error     = errors[plan.key];

            return (
              <div key={plan.key} style={s.card(plan.recommended, isCurrent)}>
                {isCurrent
                  ? <PlanBadge text={t('pricing.currentPlan')} color="#1f8a4c" />
                  : plan.recommended
                    ? <PlanBadge text={t('pricing.mostPopular')} color="#18181b" />
                    : <div style={{ height: 26, marginBottom: 16 }} />
                }

                <div style={s.planName}>{t(`pricing.${plan.key}Name`)}</div>
                <div style={{ ...s.price, fontSize: isMobile ? 28 : s.price.fontSize }}>{plan.price}</div>
                <div style={s.priceDetail}>
                  {plan.currency ? `${plan.currency} ${t('pricing.perMonth')}` : "custom pricing"}
                </div>

                <ul style={s.featureList}>
                  {plan.features.map(f => (
                    <li key={f} style={s.featureItem}>
                      <span style={s.check}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>

                {plan.isSales ? (
                  <button style={s.btnEnterprise} onClick={() => setContactOpen(true)}>
                    {t('pricing.contactUs')}
                  </button>
                ) : isCurrent ? (
                  <button style={s.btnCurrent} disabled>{t('pricing.currentPlan')}</button>
                ) : (
                  <>
                    <button
                      style={s.btnSubscribe(inFlight)}
                      disabled={inFlight}
                      onClick={() => subscribe(plan.key)}
                    >
                      {inFlight ? t('pricing.redirecting') : t('pricing.subscribe')}
                    </button>
                    {error && <div style={s.error}>{error}</div>}
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div style={s.note}>
          {t('pricing.enterpriseNote')}{" "}
          <button onClick={() => setContactOpen(true)} style={{ background: "none", border: "none", color: s.btnEnterprise.color, cursor: "pointer", fontFamily: "inherit", fontSize: "inherit", textDecoration: "underline", padding: 0 }}>
            hello@booksmarthq.io
          </button>
        </div>
      </main>

      {contactOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: 20,
        }} onClick={e => { if (e.target === e.currentTarget) setContactOpen(false); }}>
          <div style={{
            background: "#ffffff",
            border: "1px solid #e7e5e4",
            borderRadius: 8, padding: "28px 32px", maxWidth: 500, width: "100%", maxHeight: "90vh", overflowY: "auto",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#18181b", letterSpacing: "0.02em" }}>
                {t('pricing.contactFormTitle')}
              </div>
              <button onClick={() => setContactOpen(false)} style={{
                background: "none", border: "none", color: "#a1a1aa", fontSize: 20, cursor: "pointer", fontFamily: "inherit", lineHeight: 1, padding: "0 4px"
              }}>×</button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={s.planName}>{t('pricing.fullName')} *</label>
              <input style={{ ...s.input, width: "100%" }} value={contactForm.full_name}
                onChange={e => setContactForm(f => ({ ...f, full_name: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={s.planName}>{t('pricing.companyName')} *</label>
              <input style={{ ...s.input, width: "100%" }} value={contactForm.company_name}
                onChange={e => setContactForm(f => ({ ...f, company_name: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={s.planName}>{t('pricing.workEmail')} *</label>
              <input style={{ ...s.input, width: "100%" }} type="email" value={contactForm.work_email}
                onChange={e => setContactForm(f => ({ ...f, work_email: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={s.planName}>{t('pricing.bookingsCount')}</label>
              <select style={{ ...s.select, width: "100%" }} value={contactForm.bookings}
                onChange={e => setContactForm(f => ({ ...f, bookings: e.target.value }))}>
                <option value="201-500">201-500</option>
                <option value="500+">500+</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={s.planName}>{t('pricing.carriersUsed')}</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {CARRIER_OPTIONS.map(c => (
                  <label key={c} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#3f3f46", cursor: "pointer" }}>
                    <input type="checkbox" checked={contactForm.carriers.includes(c)}
                      onChange={() => toggleCarrier(c)} style={{ accentColor: "#18181b" }} />
                    {c}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={s.planName}>{t('pricing.message')}</label>
              <textarea style={{ ...s.input, width: "100%", minHeight: 80, resize: "vertical" }} value={contactForm.message}
                onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))} />
            </div>

            {contactMsg.text && (
              <div style={{
                fontSize: 11, marginTop: 10, padding: "8px 10px", borderRadius: 4, marginBottom: 16,
                color: contactMsg.type === "error" ? "#b91c1c" : "#1f8a4c",
                background: contactMsg.type === "error" ? "#fef2f2" : "#f0fdf4",
                border: `1px solid ${contactMsg.type === "error" ? "#fecaca" : "#bbf7d0"}`,
              }}>{contactMsg.text}</div>
            )}

            <button style={{ ...s.btnSubscribe(false), width: "100%" }} onClick={handleContactSubmit} disabled={contactLoading}>
              {contactLoading ? t('pricing.submitting') : t('pricing.submit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
